import {UntypedFormArray, UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {EditPermissions} from "../../../../../../../shared/models";
import {getEditTypes} from "../../../delegatorUtils";
import {newStepValue} from "../../../../../services/flowBuilder/utils/constants";
import {maxLength, required} from "../../../../../../../shared/utils";

export interface MultiProductForm {
    menuParams: UntypedFormGroup
    productIds: UntypedFormArray
}

interface MenuParams {
    bodyText: string,
    menuHeader: string,
    subHeader: string,
    buttonText: string,
    isDynamic: boolean,
    catalogId: string,
    next: string,
}


export class MultiProductNode {

    menu: any = {}

    constructor() {
        this.menu = {
            "menuType": 'multiProduct'
        }
    }

    public static toForm(stepData: { [index: string]: any }, editPermissions: EditPermissions): MultiProductForm {
        return {
            menuParams: this.menuParamsForm(this.menuParamsFromStep(stepData), editPermissions),
            productIds: this.productIdsForm(this.productIdsFromStep(stepData), editPermissions),
        }
    }

    private static menuParamsFromStep(stepData: { [index: string]: any }): MenuParams {
        const menu = stepData.menu;
        const menuParams = menu?.menuParams;
        return {
            bodyText: getValueOrDefault(menu?.bodyText),
            menuHeader: getValueOrDefault(menu?.menuHeader),
            subHeader: getValueOrDefault(menuParams?.subHeader),
            buttonText: getValueOrDefault(menuParams?.buttonText, 'View'),
            isDynamic: getValueOrDefault(menuParams?.isDynamic, false),
            catalogId: getValueOrDefault(menuParams?.catalogId),
            next: getValueOrDefault(stepData?.next, newStepValue)
        };
    }

    private static productIdsFromStep(stepData: { [index: string]: any }): string[] {
        const menu = stepData.menu;
        const menuParams = menu?.menuParams;
        const productIds = menuParams?.productIds;
        if (productIds) {
            return productIds.split(',');
        }
        return [''];
    }

    private static menuParamsForm(menuParams: MenuParams, editPermissions: EditPermissions): UntypedFormGroup {
        const {editMenuTextOnly, editFlows} = getEditTypes(editPermissions);
        return new UntypedFormGroup({
            bodyText: new UntypedFormControl({
                value: menuParams.bodyText,
                disabled: !editFlows
            }, [required('Body Text')]),
            menuHeader: new UntypedFormControl({
                value: menuParams.menuHeader,
                disabled: !editFlows
            }, [required('Menu Header'), maxLength(60, "Maximum character limit exceeded")]),
            subHeader: new UntypedFormControl({
                value: menuParams.subHeader,
                disabled: !editFlows
            }),
            buttonText: new UntypedFormControl({
                value: menuParams.buttonText,
                disabled: !editFlows
            }, [required('Button Text')]),
            isDynamic: new UntypedFormControl({
                value: menuParams.isDynamic,
                disabled: !editFlows
            }),
            catalogId: new UntypedFormControl({
                value: menuParams.catalogId,
                disabled: !editFlows
            }, [required('Catalogue ID')]),
            next: new UntypedFormControl(menuParams.next),
        })
    }

    public static productIdTemplate(productId: string, editPermissions: EditPermissions) {
        const {editMenuTextOnly, editFlows} = getEditTypes(editPermissions);
        return new UntypedFormGroup({
                id: new UntypedFormControl({
                    value: productId,
                    disabled: !editFlows
                }, [required('Product ID')])
            }
        )
    }

    private static productIdsForm(productIds: string[], editPermissions: EditPermissions): UntypedFormArray {
        const productIdsFormArray: UntypedFormArray = new UntypedFormArray([]);
        for (const productId of productIds) {
            productIdsFormArray.push(this.productIdTemplate(productId, editPermissions))
        }
        return productIdsFormArray
    }

    private addMenu(bodyText: string, menuHeader: string) {
        this.menu = {
            ...this.menu,
            bodyText,
            menuHeader
        }
    }

    private addMenuParams(subHeader: string, buttonText: string, isDynamic: boolean, catalogId: string) {
        const menuParams = {
            subHeader,
            buttonText,
            isDynamic,
            catalogId
        }
        this.menu = {
            ...this.menu,
            menuParams
        }
    }

    private addProductIds(ids: string[]) {
        const productIds: string = ids.join(",");
        const menuParams = {
            ...this.menu.menuParams,
            productIds
        }
        this.menu = {
            ...this.menu,
            menuParams
        }
    }

    public getNodeData(multiProductForm: MultiProductForm): {} {

        const {menuParams, productIds} = multiProductForm

        const menuParamsRawData: MenuParams = menuParams.getRawValue();
        const productIdsRawData = productIds.getRawValue().map(products => products.id);

        this.addMenu(menuParamsRawData.bodyText, menuParamsRawData.menuHeader);
        this.addMenuParams(menuParamsRawData.subHeader, menuParamsRawData.buttonText, menuParamsRawData.isDynamic, menuParamsRawData.catalogId)

        if (menuParamsRawData.isDynamic) {
            this.addProductIds([productIdsRawData[0]])
        } else {
            this.addProductIds(productIdsRawData)
        }

        return {
            menu: this.menu,
            'type': 'menu',
            'next': menuParamsRawData.next,
            metadata: {
                nodeVersion: '1.0.0'
            }
        };
    }

}

// # Predefined
// step1:
//   menu:
//     menuType: "multiProduct"
//     bodyText: "" // Required
//     menuHeader: "" // Required
//     menuParams:
//       subHeader: "" // Optional
//       buttonText: "View" // Required. Defaults to "View"
//       isDynamic: false // Determines if the product ids are a predefined list or not
//       productIds: id1,id2,id3
//   type: "menu"
//   metadata: {}
//


const getValueOrDefault = (value, defaultValue: any = ''): any => {
    return value ? value : defaultValue
}
